import mapboxgl, {
  PointLike,
} from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useEffect, useMemo, useRef, useState } from "react";
import Map, { Layer, MapRef, Marker, Source } from "react-map-gl";
import "./mapCss.css";
import { MapFeatures} from "./Features/MapFeatures";
import { useHandleOnClick } from "./Features/useHandleOnClick";
import { mapSettings } from "./mapSettings";
import { MapPopupMultipleFeatures } from "./Overlays/MapPopup";
import { lang } from "src/language/Strings";
import useChangeTitle from "src/api/hooks/useChangeTitle";
import { useAppContext } from "src/components/Context";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "src/components/Buttons/Button";
import { routes } from "src/routes";
import FilterButton from "src/components/FilterButton";
import { useClosestPoints } from "./useClosestPoints";
import { usePosition } from "src/api/hooks/usePosition";
import { UserPositionMarker } from "./UserPositionMarker";
import { featureColor } from "./featureColor";
import useMyUser from "src/api/hooks/useMyUser";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { apiUrls } from "src/consts/ApiUrls";
import { portalApiClient } from "src/api/apiClient";
import { LocalStorageKeys } from "src/utils/localStorageKeys";
import { useCompletionsForNearestDay, useCompletionsForNearestWeek } from "src/api/hooks/useCompletionsForNearestWeek";
import useProjectVariations from "src/api/hooks/useProjectVariations";
import { useMapFiles } from "./Features/GeoJSONFiles";
import { LegendContainer } from "./LegendContainer";  
import { useStore } from "src/components/Store";

function getPolygonCenter(geometry: any): [number, number] | null {
  if (!geometry || !geometry.coordinates) return null;

  let allCoords: number[][] = [];

  if (geometry.type === "Polygon") {
    allCoords = geometry.coordinates[0] || [];
  } else if (geometry.type === "MultiPolygon") {
    allCoords = geometry.coordinates.flat().flat();
  }

  if (allCoords.length === 0) return null;

  const sumLng = allCoords.reduce((sum, coord) => sum + coord[0], 0);
  const sumLat = allCoords.reduce((sum, coord) => sum + coord[1], 0);

  return [sumLng / allCoords.length, sumLat / allCoords.length];
}
function isInBoundingBox(bbox: [PointLike, PointLike], point: PointLike) {
  return (
    point[0] >= bbox[0][0] &&
    point[0] <= bbox[1][0] &&
    point[1] >= bbox[0][1] &&
    point[1] <= bbox[1][1]
  );
}

export function InspectionMap() {
  useChangeTitle(lang.map);
  const { mapRef, setMapRef } = useAppContext();
  const handleOnClick = useHandleOnClick();
  const { deviationId, inspectionId } = useParams();
  const MapRefObject = useRef<MapRef | null>(null);
  const [clickedFeatures, setClickedFeatures] = useState<any[]>([]);
  const store = useStore();
  const [clickCoordinates, setClickCoordinates] = useState<{
    lng: number;
    lat: number;
  } | null>(null);
  const pointsWithinMeters = useClosestPoints();
  useEffect(() => {
    setMapRef(MapRefObject);
  }, [setMapRef]);
  const nav = useNavigate();
  const {data: myUser} = useMyUser();
  const userPosition = usePosition();
  const { companyId } = useParams();
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  // Show location prompt if needed
  useEffect(() => {
    if (userPosition.shouldShowPrompt) {
      setShowLocationPrompt(true);
    }
  }, [userPosition.shouldShowPrompt]);
  useEffect(() => {
    if (mapRef?.current) {

    }
  },[mapRef?.current, userPosition.latitude, userPosition.longitude, userPosition.accuracy, pointsWithinMeters])

  const {data: deviations} = useProjectVariations();
  const {data: mapFiles} = useMapFiles();
  useEffect(() => {
    console.log("Map files: ",mapFiles)
  },[mapFiles?.length])

  useEffect(() => {
    if (mapRef?.current) {
      const mapInstance = mapRef.current.getMap();
      if (!mapInstance) {
        return;
      }
      const mapLayerIds =
        mapFiles?.reduce<string[]>((acc, file: any) => {
          const baseId = file?.depiction;
          if (!baseId) return acc;
          acc.push(`${baseId}-points`);
          acc.push(`${baseId}-fill`);
          acc.push(`${baseId}-stroke`);
          acc.push(`${baseId}-lines`);
          return acc;
        }, []) ?? [];

      const mapLayerIdsToHide = mapLayerIds.filter(
        (layerId) => !store.displayedMapLayers.includes(layerId.split("-")[0])
      );

      // Show layers that should be visible
      mapLayerIds
        .filter((layerId) => !mapLayerIdsToHide.includes(layerId))
        .forEach((layerId) => {
          if (mapInstance.getLayer(layerId)) {
            mapInstance.setLayoutProperty(layerId, "visibility", "visible");
          }
        });

      // Hide layers that are not in the displayed map layers but in the mapFiles
      mapLayerIdsToHide.forEach((layerId) => {
        if (mapInstance.getLayer(layerId)) {
          mapInstance.setLayoutProperty(layerId, "visibility", "none");
        }
      });
    }
  },[
    store.displayedMapLayers.length
  ])
  return (
    <>
      {(inspectionId || deviationId) && (
        <div className="w-full h-10 flex justify-end items-center top-parent absolute z-20">
          <Button
            title={lang.showAllObjects}
            className="mt-1 bg-White bg-opacity-80"
            onClick={() => nav(routes.Map.url(companyId!))}
            toast={{
              message: "Visar nu alla objekt",
              messageType: "success",
              timeout: 3000,
            }}
          />
        </div>
      )}
      <LegendContainer />
      <div className="h-full w-full">
        <Map
          ref={MapRefObject}
          mapboxAccessToken={
            "pk.eyJ1IjoiZ3JlZW4tZGlnaXRhbGlzZXJpbmciLCJhIjoiY2xyZHdqZnJ1MTk3ODJqbXd1aW5qamFnNyJ9.H02WJFskLo_qSlnyZwwq0A"
          }
          preserveDrawingBuffer={true}
          initialViewState={{
            longitude: mapSettings.defaultMapCenter[0],
            latitude: mapSettings.defaultMapCenter[1],
            zoom: mapSettings.defaultZoom,
          }}
          mapStyle={mapSettings.backgroundMap}
          projection={{ name: "mercator" }}
          onClick={(e) => {
            setClickCoordinates({ lng: e.lngLat.lng, lat: e.lngLat.lat });
            handleOnClick(e, setClickedFeatures);
          }}
          cursor="pointer"
        >

          <MapFeatures />
          
          {clickedFeatures &&
            clickedFeatures.length > 0 &&
            (() => {
              const feature = clickedFeatures[0];
              let longitude: number;
              let latitude: number;

              if (feature.geometry.type === "Point") {
                longitude = feature.geometry.coordinates[0];
                latitude = feature.geometry.coordinates[1];
              } else {
                // For polygons, use click coordinates if available, otherwise calculate center
                if (clickCoordinates) {
                  longitude = clickCoordinates.lng;
                  latitude = clickCoordinates.lat;
                } else {
                  const center = getPolygonCenter(feature.geometry);
                  longitude = center?.[0] || 0;
                  latitude = center?.[1] || 0;
                }
              }

              return (
                <MapPopupMultipleFeatures
                  features={clickedFeatures}
                  longitude={longitude}
                  latitude={latitude}
                  onClose={() => {
                    setClickedFeatures([]);
                    setClickCoordinates(null);
                  }}
                />
              );
            })()}
          <UserPositionMarker />
          <OverlayPointsWithinMeters onSelect={(f) => {
            setClickedFeatures(f)}
            } />
          <ClickedFeatures features={clickedFeatures} />
        </Map>
      </div>
    </>
  );
}


function OverlayPointsWithinMeters({ onSelect }: { onSelect: (features: any[]) => void }) {
  const pointsWithinMeters = useClosestPoints();
  const {companyId} = useParams();
  const {data: completions} = useCompletionsForNearestDay();
  const nav = useNavigate();
  const completionMutation = useMutation({
    mutationKey: ["completion"],
    mutationFn: async (payload: any) => {
      const response = await portalApiClient.post(apiUrls.completion.create.url(), payload)
      return response.data;
    }
  })
  
  if (pointsWithinMeters.length === 0) {
    return null;
  }
  const feature = pointsWithinMeters.find(f => !completions?.some(c => c.externalId === f?.properties?.OBJECTID));
  if (feature?.properties?.featureType?.includes("MapFileFeature")) {
    return <div 
    style={{
      opacity: completionMutation.isPending ? 0 : 1,
      transition: "opacity 0.3s ease-in-out, display 0.3s ease-in-out",
    }}
    className={`absolute flex-col justify-center items-center bg-White bg-opacity-50 bottom-0 left-0 w-full h-56 z-30 flex`}>

    <div className="w-full h-10 flex items-center align-middle justify-center">
      <span className="text-xl">({feature?.properties?.distance?.toFixed(2)} m)</span>
      </div>

      <div className="w-[80%] h-16 border-2 border-PrimaryBlack rounded-[50px] flex flex-row">

      </div>
      <div className="w-full h-12 flex items-center align-middle justify-center">
        <button title={lang.deviation} className="text-PrimaryBlack text-lg 
        hover:text-Grey9 p-2 rounded-[50px] border-2 border-Grey9 mt-2 active:scale-95 active:bg-Grey1" onClick={() => {
          localStorage.setItem(LocalStorageKeys.defaultDeviationInfo, `ObjektID: ${feature?.properties?.OBJECTID}, 
            Skotselkod: ${feature?.properties?.Skotselkod}`)
          nav(routes.Deviation.Pages.Create.url(companyId!));
        }} ><span className="text-lg">{lang.reportDeviation}</span></button>
      </div>
    </div>;
  }
}

function ClickedFeatures({ features }: { features: any[] }) {
  const maxLength = 20;
  return (
    <>
      <Source
        id="clicked-features"
        type="geojson"
        data={{ type: "FeatureCollection", features: features.slice(0, maxLength) }}
      >
        <Layer
          id="clicked-features-layer"
          type="circle"
          source="clicked-features"
          paint={{
            // Keep the underlying marker color but make clicked features
            // stand out clearly from nearby points.
            "circle-color": ["get", "markerColor"],
            "circle-radius": 14,
            "circle-opacity": 0.9,
            "circle-stroke-width": 3.5,
            "circle-stroke-color": "#000000",
            "circle-stroke-opacity": 0.9,
          }}
        />
      </Source>
    </>
  );
}
